import {
  GetConfigResponseType,
  PaymentFrequenciesType,
  SelectOption,
} from 'QuoteApp/services/endpoints/GetConfig';
import { GetDetailsResponseType } from 'QuoteApp/services/endpoints/GetDetails';
import { FlexiblePaymentFrequency } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { formatText, getWeekDay, toCamelCase, toSnakeCase } from 'Shared/utils';

import { PriceCustomizerFormValues } from '../domain';
import { fromInputToOutput, fromOutputToInput } from '../mapper';
import { getConfigByFrequency } from '../utils';

type GetPriceCustomizerInitialValuesParams = {
  details: GetDetailsResponseType;
  config: GetConfigResponseType | null;
};

type FlexiblePaymentPreferencesPayload = {
  flexiblePaymentPreferences: {
    frequency: string;
    days_of_month?: number[];
    days_of_week?: string[];
  };
};

const getDefaultWeeklyPaymentDay = (
  effectiveStartDate: string,
  paymentFrequencies?: PaymentFrequenciesType
) => {
  const effectiveDate = new Date(effectiveStartDate);

  if (isNaN(effectiveDate.getTime())) return undefined;

  const weekdayConfig = getConfigByFrequency(
    'weekly',
    paymentFrequencies
  ) as Array<SelectOption<string>>;

  const effectiveWeekDay = formatText(getWeekDay(effectiveDate), 'lowercase');

  const match = weekdayConfig.find(
    (option) => option.value === effectiveWeekDay
  );

  if (!match) return undefined;

  return match.value;
};

const getDefaultBiweeklyPaymentDay = (
  effectiveStartDate: string,
  paymentFrequencies?: PaymentFrequenciesType
) => {
  const effectiveDate = new Date(effectiveStartDate);

  if (isNaN(effectiveDate.getTime())) return undefined;

  const weekdayConfig = getConfigByFrequency(
    'biWeekly',
    paymentFrequencies
  ) as Array<SelectOption<string>>;

  const effectiveWeekDay = formatText(getWeekDay(effectiveDate), 'lowercase');

  const match = weekdayConfig.find(
    (option) => option.value === effectiveWeekDay
  );

  if (!match) return undefined;

  return match.value;
};

const getDefaultSemiMonthlyPaymentDays = (
  effectiveStartDate: string,
  paymentFrequencies?: PaymentFrequenciesType
) => {
  const effectiveDate = new Date(effectiveStartDate);

  if (isNaN(effectiveDate.getTime())) return undefined;

  const semiMonthlyConfig = getConfigByFrequency(
    'semiMonthly',
    paymentFrequencies
  ) as Array<SelectOption<Array<number>>>;

  if (semiMonthlyConfig.length === 0) return undefined;

  const effectiveDay = effectiveDate.getUTCDate();

  // There are scenarios where could exist duplicated values
  // 1,15 and 15,29, in this example day 15 is the duplicated value,
  // if that is the case we want to return the first instance where the
  // effective day is found
  const match = semiMonthlyConfig.find((option) =>
    option.value.includes(effectiveDay)
  );

  if (!match) {
    const defaultDays = [1, 15];

    const defaultValue =
      semiMonthlyConfig.find((option) =>
        defaultDays.some((day) => option.value.includes(day))
      )?.value ?? semiMonthlyConfig[0].value;

    return defaultValue.join(',');
  }

  return match.value.join(',');
};

const getDefaultMonthlyPaymentDay = (
  effectiveStartDate: string,
  paymentFrequencies?: PaymentFrequenciesType
) => {
  const effectiveDate = new Date(effectiveStartDate);

  if (isNaN(effectiveDate.getTime())) return undefined;

  const monthlyConfig = getConfigByFrequency(
    'monthly',
    paymentFrequencies
  ) as Array<SelectOption<number>>;

  const effectiveDay = effectiveDate.getUTCDate();

  const match = monthlyConfig.find((option) => option.value === effectiveDay);

  if (!match) return undefined;

  return match.value.toString();
};

export const getPriceCustomizerInitialValues = ({
  details,
  config,
}: GetPriceCustomizerInitialValuesParams): PriceCustomizerFormValues => {
  const { frequency, daysOfWeek, daysOfMonth } =
    details?.flexiblePaymentPreferences || {};

  const effectiveStartDate = details?.effectiveDate?.value;
  const paymentFrequencies = config?.flexiblePayments?.paymentFrequencies;

  const {
    frequency: initialFrequency,
    dayOfWeek = getDefaultWeeklyPaymentDay(
      effectiveStartDate,
      paymentFrequencies
    ),
    biweeklyDate = getDefaultBiweeklyPaymentDay(
      effectiveStartDate,
      paymentFrequencies
    ),
    semiMonthlyDates = getDefaultSemiMonthlyPaymentDays(
      effectiveStartDate,
      paymentFrequencies
    ),
    monthly = getDefaultMonthlyPaymentDay(
      effectiveStartDate,
      paymentFrequencies
    ),
  } = fromInputToOutput({
    frequency: toCamelCase(frequency) as FlexiblePaymentFrequency,
    daysOfWeek: daysOfWeek ? daysOfWeek.map(String) : [],
    daysOfMonth: daysOfMonth ? daysOfMonth.map(Number) : [],
  });

  return {
    frequency: initialFrequency,
    dayOfWeek,
    biweeklyDate,
    semiMonthlyDates,
    monthly,
  };
};

export const buildFlexiblePaymentPreferencesPayload = (
  formValues: Partial<PriceCustomizerFormValues>
): FlexiblePaymentPreferencesPayload => {
  const { frequency, daysOfMonth, daysOfWeek } = fromOutputToInput(formValues);

  if (frequency === 'fullPay') {
    return {
      flexiblePaymentPreferences: {
        frequency: toSnakeCase(frequency),
      },
    };
  }

  if (frequency.toLowerCase().includes('monthly')) {
    return {
      flexiblePaymentPreferences: {
        frequency: toSnakeCase(frequency),
        days_of_month: daysOfMonth ? daysOfMonth.map(Number) : [],
      },
    };
  }

  return {
    flexiblePaymentPreferences: {
      frequency: toSnakeCase(frequency),
      days_of_week: daysOfWeek ? daysOfWeek.map(String) : [],
    },
  };
};
